<template>
  <div class="screen shop">
    <div class="shop__topbar">
      <button class="shop__terug" type="button" @click="goHome">
        <svg width="18" height="13" viewBox="0 0 20 14" fill="none" aria-hidden="true">
          <path
            d="M19 7H2M8 1L2 7l6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Terug naar spel
      </button>
      <span class="shop__week">Week {{ store.weekNumber }}/{{ store.weeksTotal }}</span>
      <StarCounter :count="remaining" tone="dark" :icon-size="24" :filled="false" />
    </div>

    <!-- Nothing to bet with (§10) — Figma 443:496. The prize list is pointless
         here and the confirm bar stays disabled, so the journey cannot be spent
         on an empty shop and dropped into the opt-in flow. -->
    <div v-if="!store.starBalance" class="shop__body shop__body--empty">
      <img :src="emptyStar" class="shop__empty-star" alt="" aria-hidden="true" />
      <h1 class="shop__empty-title">Je hebt nog geen sterren</h1>
      <p class="shop__empty-copy">Speel eerst een spel om sterren te verdienen.</p>
      <button class="shop__empty-cta" type="button" @click="goHome">Terug naar spellen</button>
    </div>

    <div v-else class="shop__body">
      <h1 class="shop__title">Zet je sterren in voor leuke prijzen!</h1>
      <p class="shop__lead">
        Gebruik je sterren als loten en zet ze in op prijzenpotten naar keuze.
      </p>

      <ul class="shop__list">
        <li v-for="p in store.prizes" :key="p.id" class="prize-card">
          <div class="prize-card__head" :style="headStyle(p)">
            <div class="prize-card__meta">
              <h3>{{ p.title }}</h3>
              <p>{{ p.subtitle }}</p>
            </div>
            <div class="prize-card__img">
              <img :src="p.image" :alt="p.title" />
            </div>
          </div>

          <div class="prize-card__foot" :class="{ 'is-open': openInfo[p.id] }">
            <button
              class="prize-card__info"
              type="button"
              :aria-expanded="!!openInfo[p.id]"
              @click="toggleInfo(p.id)"
            >
              {{ openInfo[p.id] ? 'Minder info' : 'Meer info' }}
              <img
                :src="caretIcon"
                class="prize-card__caret"
                :class="{ 'is-open': openInfo[p.id] }"
                alt=""
                aria-hidden="true"
              />
            </button>

            <div class="stepper">
              <button
                class="stepper__btn stepper__btn--minus"
                type="button"
                aria-label="Minder sterren"
                :disabled="!draft[p.id]"
                @click="dec(p.id)"
              >
                <img :src="minusIcon" width="24" height="24" alt="" aria-hidden="true" />
              </button>
              <span class="stepper__val">{{ draft[p.id] || 0 }}</span>
              <button
                class="stepper__btn stepper__btn--plus"
                type="button"
                aria-label="Meer sterren"
                :disabled="remaining <= 0"
                @click="inc(p.id)"
              >
                <img :src="plusIcon" width="24" height="24" alt="" aria-hidden="true" />
              </button>
            </div>
          </div>

          <!-- The panel opens by animating a grid track from 0fr to 1fr, so it
               eases to its real height without that height being known up front. -->
          <div class="prize-card__info-wrap" :class="{ 'is-open': openInfo[p.id] }">
            <div class="prize-card__info-clip">
              <div class="prize-card__info-panel">
                <h4 v-if="p.infoTitle" class="prize-card__info-title">{{ p.infoTitle }}</h4>
                <!-- CMS formatting (bold, lists, links), sanitized again in services/safeHtml.js -->
                <div class="prize-card__info-body" v-html="safeInfoHtml(p.info)" />
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div class="shop__bar">
      <button
        class="shop__confirm"
        type="button"
        :disabled="busy || !store.starBalance"
        @click="confirm"
      >
        Sterren inzetten
      </button>
    </div>

    <!-- Confirms before leaving the shop: turns still to play (§9) takes
         precedence over stars still to place (§10.1) — it is the bigger loss. -->
    <transition name="fade">
      <div v-if="warn" class="shop__overlay">
        <div class="shop__modal">
          <template v-if="warn === 'turns'">
            <h2>Je mag nog meer spellen spelen deze week</h2>
            <p>
              Weet je zeker dat je door wilt gaan? Als je doorgaat dan verlies je de overige spellen
              van deze week.
            </p>
          </template>
          <template v-else>
            <h2>Je hebt nog niet al je sterren ingezet</h2>
            <p>Weet je zeker dat je door wilt gaan? Niet-ingezette sterren blijven beschikbaar.</p>
          </template>
          <div class="shop__modal-actions">
            <button class="modal-btn modal-btn--ghost" type="button" @click="warn = null">
              Toch blijven
            </button>
            <button class="modal-btn modal-btn--primary" type="button" @click="proceed">
              Doorgaan
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import { safeInfoHtml } from '@/services/safeHtml'
import StarCounter from '@/components/ui/StarCounter.vue'
// Figma 443:319 / 443:322 / 443:327 — the design's own caret and stepper glyphs
import caretIcon from '@/assets/v3/icon-caret.svg'
import minusIcon from '@/assets/v3/icon-minus.svg'
import plusIcon from '@/assets/v3/icon-plus.svg'
// Figma 443:525 — the empty-state star outline
import emptyStar from '@/assets/v3/shop-empty-star.svg'

const store = useGameStore()
const router = useRouter()

const draft = reactive({})
const openInfo = reactive({})
const busy = ref(false)
const warn = ref(null) // null | 'turns' | 'stars'

onMounted(() => store.loadShop())

const staged = computed(() => Object.values(draft).reduce((a, n) => a + (n || 0), 0))
const remaining = computed(() => store.starBalance - staged.value)

function headStyle(p) {
  if (p.bgImage) {
    return {
      backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.35) 38%, rgba(0,0,0,0) 74%), url(${p.bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }
  }
  return { background: p.color || 'var(--c-seg-navy)' }
}
function inc(id) {
  if (remaining.value <= 0) return
  draft[id] = (draft[id] || 0) + 1
}
function dec(id) {
  if (!draft[id]) return
  draft[id] -= 1
}
function toggleInfo(id) {
  openInfo[id] = !openInfo[id]
}

function confirm() {
  // Leaving the shop ends the week's journey (opt-ins → eindscherm), so warn while
  // there are still games to play. That warning wins over the unplaced-stars one:
  // unplaced stars stay available, unplayed games do not.
  if (store.hasTurns) {
    warn.value = 'turns'
    return
  }
  if (store.starBalance > 0 && remaining.value > 0) {
    warn.value = 'stars'
    return
  }
  proceed()
}
async function proceed() {
  warn.value = null
  busy.value = true
  await store.confirmShop({ ...draft })
  busy.value = false
  router.replace({ name: store.pendingOptinSteps.length ? 'optin' : 'eind' })
}

function goHome() {
  router.replace({ name: 'home' })
}
</script>

<style lang="scss" scoped>
.shop {
  // Bound the column to the viewport (.screen only sets a min-height) so the
  // list scrolls INSIDE it and the "Sterren inzetten" bar stays on screen —
  // it has to be reachable the moment the counter hits 0.
  height: 100vh;
  height: 100dvh;
  background: #e6e6e6; // Figma 443:386 grey band behind the topbar
}
.shop__topbar {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  // top row now that the header is gone — sits at the top, notch-safe
  padding: max(env(safe-area-inset-top), var(--sp-3)) var(--sp-4) var(--sp-3);
}
.shop__terug {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  font-family: var(--font-alt);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: none;
  border-radius: var(--r-badge);
}
.shop__week {
  padding: 7px 12px;
  font-size: var(--fs-md);
  font-weight: var(--fw-bold);
  color: var(--c-black);
  background: var(--c-white);
  border: 1px solid var(--c-border);
  border-radius: var(--r-pill);
}
.shop__topbar :deep(.star-counter) {
  margin-left: auto;
}
.shop__body {
  flex: 1 1 auto;
  min-height: 0; // shrink instead of pushing the bar off-screen
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--sp-6) var(--sp-4) var(--sp-5);
  background: var(--c-white);
  border-radius: 20px 20px 0 0;
}
.shop__title {
  max-width: 254px; // Figma 443:485 — wraps to two lines
  margin: 0 auto;
  font-size: 23px; // Figma 443:485
  line-height: 1.33;
  color: var(--c-black);
  text-align: center;
}
.shop__lead {
  margin: var(--sp-3) auto 0;
  max-width: 261px; // Figma 443:484
  font-size: var(--fs-base);
  line-height: 1.44;
  color: var(--c-black);
  text-align: center;
}

// --- Empty state (Figma 443:524) ---------------------------------------------
// The block sits near the top of the sheet in the design (star at y=217 on a
// sheet starting at 158), not centred in it, so it keeps the same padding as the
// list does and just stacks.
.shop__body--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 54px; // Figma 443:525 — the star sits 54.5 below the sheet's top edge
  text-align: center;
}
.shop__empty-star {
  display: block;
  width: 196px; // Figma 443:525 — 187 wide plus its 9px stroke
  height: 188px;
}
.shop__empty-title {
  max-width: 293px; // Figma 443:527
  margin-top: 33px;
  font-size: 23px;
  line-height: 1.33;
  color: var(--c-black);
}
.shop__empty-copy {
  max-width: 222px; // Figma 443:526
  margin-top: var(--sp-2);
  font-size: var(--fs-lg);
  line-height: 1.44;
  color: var(--c-black);
}
.shop__empty-cta {
  width: 252px; // Figma 443:529 — 252×60, radius 18
  max-width: 100%;
  height: 60px;
  margin-top: 24px;
  font-family: var(--font-brand);
  font-size: var(--fs-xl);
  font-weight: var(--fw-bold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: none;
  border-radius: var(--r-lg);
}

.shop__list {
  list-style: none;
  margin: var(--sp-6) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.prize-card {
  border-radius: 14px;
}
.prize-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  min-height: 110px;
  padding: var(--sp-5) 10px var(--sp-5) var(--sp-5);
  border: 1px solid var(--c-black);
  border-bottom: none;
  border-radius: 14px 14px 0 0;
  overflow: hidden;
}
.prize-card__meta {
  flex: 1 1 0;
  min-width: 0;
  color: var(--c-white);

  h3 {
    font-size: var(--fs-base);
    line-height: 1.2;
  }
  p {
    margin-top: 5px;
    font-size: var(--fs-sm);
    font-weight: var(--fw-regular);
    line-height: 1.28;
    color: rgba(255, 255, 255, 0.92);
  }
}
.prize-card__img {
  flex: none;
  width: 125px; // Figma 443:314 image column
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;

  // Figma 443:315 draws the artwork 102×86 INSIDE that column, with the source's
  // transparent border cropped away. The prize art is trimmed to its ink for the
  // same reason, so these two numbers are what the artwork actually fills.
  img {
    max-width: 102px;
    max-height: 86px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35));
  }
}

.prize-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: var(--sp-2);
  background: var(--c-white);
  border: 1px solid var(--c-black);
  border-radius: 0 0 14px 14px;
  transition: border-radius var(--dur-base) var(--ease-out);

  // when the info panel is open it takes the rounded bottom (Figma 443:444)
  &.is-open {
    border-radius: 0;
  }
}
.prize-card__info {
  display: inline-flex;
  align-items: center;
  gap: 5px; // Figma 443:317
  height: 35px;
  padding: 0 10px; // Figma 443:317 — the pill measures 94 wide
  font-family: var(--font-brand);
  font-size: var(--fs-sm);
  font-weight: var(--fw-bold);
  color: var(--c-black);
  background: none;
  border: 1px solid var(--c-black);
  border-radius: 12px;
}
// Figma 443:319 — an 8×5 triangle with subtly rounded corners, drawn pointing
// up and rotated 180° for the closed state (a CSS border triangle is dead sharp).
.prize-card__caret {
  display: block;
  width: 8px;
  height: 5px;
  transform: rotate(180deg);
  transition: transform var(--dur-base) var(--ease-out);
  &.is-open {
    transform: rotate(0deg);
  }
}

.stepper {
  display: flex;
  align-items: stretch;
  gap: 5px;
  width: 150px;
  height: 35px;
}
.stepper__btn {
  flex: 1 1 0;
  min-width: 0; // the UA button padding would otherwise widen it past a third
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 12px; // Figma 443:321/326

  &--minus {
    background: var(--c-border); // Figma #d8d8d8
  }
  &--plus {
    background: var(--c-blue-deep); // Figma #001389
  }
  &:disabled {
    opacity: 0.45;
  }
  img {
    display: block;
  }
}
.stepper__val {
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px; // Figma 443:427
  font-weight: var(--fw-bold);
  color: var(--c-black);
  border: 1px solid var(--c-black);
  border-radius: 12px;
}

// Animating to a height nobody knows in advance: the wrapper is a grid whose
// single row goes 0fr → 1fr, which the browser interpolates for us. The clip in
// between is what hides the panel's own border while the row is closed.
.prize-card__info-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--dur-base) var(--ease-out);

  &.is-open {
    grid-template-rows: 1fr;
  }
}
.prize-card__info-clip {
  overflow: hidden;
  min-height: 0;
}

.prize-card__info-panel {
  // Figma 443:458 — bordered white panel that carries the card's rounded bottom
  padding: var(--sp-5);
  background: var(--c-white);
  border: 1px solid var(--c-black);
  border-top: none;
  border-radius: 0 0 14px 14px;
}
.prize-card__info-title {
  font-size: var(--fs-md); // Figma 443:460 — 15px bold
  font-weight: var(--fw-bold);
  line-height: 1.21;
  color: var(--c-black);
}
.prize-card__info-body {
  margin-top: 5px; // Figma gap-[5px]
  font-size: var(--fs-sm);
  font-weight: var(--fw-regular);
  line-height: 18px;
  color: var(--c-black);

  // The CMS text is HTML (v-html), so its elements carry no scoped attribute: reach them with :deep.
  :deep(p),
  :deep(ul),
  :deep(ol) {
    margin: 0 0 6px;
  }
  :deep(ul),
  :deep(ol) {
    padding-left: 18px;
  }
  :deep(ul) {
    list-style: disc;
  }
  :deep(ol) {
    list-style: decimal;
  }
  :deep(h2),
  :deep(h3) {
    margin: 8px 0 2px;
    font-size: var(--fs-md);
    font-weight: var(--fw-bold);
  }
  :deep(strong),
  :deep(b) {
    font-weight: var(--fw-bold);
  }
  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }
  & > :deep(:last-child) {
    margin-bottom: 0;
  }
}

.shop__bar {
  flex: none;
  padding: var(--sp-4);
  padding-bottom: max(var(--sp-4), env(safe-area-inset-bottom));
  background: var(--c-white);
  border-top: 1px solid rgba(0, 0, 0, 0.19);
}
.shop__confirm {
  width: 100%;
  height: 60px;
  font-family: var(--font-brand);
  font-size: var(--fs-xl);
  font-weight: var(--fw-bold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: none;
  border-radius: var(--r-lg);
  // Figma 443:521 — the same grey-on-grey the status bar's disabled action uses,
  // not a faded blue: with nothing to bet the bar is plainly out of play.
  &:disabled {
    color: #868686;
    background: #cdcdcd;
  }
}

.shop__overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-5);
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(6px);
}
.shop__modal {
  width: 100%;
  max-width: 328px;
  padding: 25px;
  color: var(--c-ink);
  background: var(--c-white);
  border-radius: 28px;

  h2 {
    font-size: var(--fs-xl);
  }
  p {
    margin-top: 10px;
    font-weight: var(--fw-regular);
    color: var(--c-ink);
  }
}
.shop__modal-actions {
  display: flex;
  gap: 10px;
  margin-top: var(--sp-5);
}
.modal-btn {
  flex: 1 1 0;
  height: 56px;
  font-family: var(--font-brand);
  font-size: var(--fs-lg);
  font-weight: var(--fw-bold);
  border-radius: 18px;

  &--ghost {
    color: var(--c-black);
    background: none;
    border: 1px solid var(--c-black);
  }
  &--primary {
    color: var(--c-white);
    background: var(--c-blue-deep);
    border: none;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--dur-fast);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
